import { PqClient, PqServiceLevel, PqServiceStatus, type PqUser } from './pq-client';
import { LoginRequiredError } from '@workos-inc/authkit-react';

export class AccountLoadError extends Error {
    constructor(message: string, public readonly requiresSignIn = false) {
        super(message);
        this.name = 'AccountLoadError';
    }
}

export async function getAccountAccessToken(getAccessToken: () => Promise<string | undefined>): Promise<string> {
    try {
        const token = await getAccessToken();
        if (token) return token;
    } catch (error) {
        if (!(error instanceof LoginRequiredError)) throw error;
    }
    throw new AccountLoadError('Your session has expired. Please sign in again.', true);
}

export async function fetchAccount(
    getAccessToken: () => Promise<string | undefined>,
    signal?: AbortSignal,
    client = new PqClient(),
): Promise<PqUser> {
    const token = await getAccountAccessToken(getAccessToken);
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const response = await client.get<PqUser>(token, '/mobile/me', { returnResponse: true, signal });
    if (!response.ok) {
        if (response.status === 401) throw new AccountLoadError('Your session has expired. Please sign in again.', true);
        // A valid WorkOS user whose login isn't linked to a mobile-enabled ParcelQuest account.
        if (response.status === 403) throw new AccountLoadError("This account isn't mobile-enabled. Contact ParcelQuest support if you think this is a mistake.");
        throw new AccountLoadError('Unable to load your account. Check your connection and try again.');
    }
    const account = response.data;
    if (!account || typeof account !== 'object'
        || (account.serviceStatus !== null && !Object.values(PqServiceStatus).includes(account.serviceStatus as PqServiceStatus))
        || (account.serviceLevel !== null && !Object.values(PqServiceLevel).includes(account.serviceLevel as PqServiceLevel))) {
        throw new AccountLoadError('Account information is unavailable. Please try again or contact support.');
    }
    // Billing controls stay hidden unless the API explicitly grants them.
    return { ...account, canManageBilling: account.canManageBilling === true };
}

/** Where an account belongs: the app when active, otherwise billing (if it can manage it) or support. */
export function accountDestination(account: PqUser): string {
    if (account.serviceStatus === PqServiceStatus.Active) return '/';
    return account.canManageBilling ? '/billing' : '/support';
}

export function accountRedirect(account: PqUser, pathname: string): string | null {
    const destination = accountDestination(account);
    if (pathname === '/callback') return destination;
    if (pathname === '/billing' && !account.canManageBilling) return destination;
    return account.serviceStatus !== PqServiceStatus.Active && pathname !== destination ? destination : null;
}
